import { useRef, useState } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { bearingDegrees } from '@/lib/geo'

// #247 — the Map | Street View switch's logic, lifted from the event popup (#243) so the
// camera modal can use the same behaviour: look a panorama up within 50 m, open it facing
// the point, or say there is none and stay on the map.

// How far from the point a panorama may be and still count as "here".
export const STREET_VIEW_RADIUS_M = 50

export function useStreetViewSwitch(map: google.maps.Map | null, at: { lat: number; lng: number }) {
  const streetViewLib = useMapsLibrary('streetView')
  const [view, setView] = useState<'map' | 'street'>('map')
  const [lookup, setLookup] = useState<'idle' | 'checking' | 'none'>('idle')
  // Bumped whenever an answer from Google would no longer be wanted.
  const requestRef = useRef(0)

  const showMap = () => {
    requestRef.current += 1
    map?.getStreetView().setVisible(false)
    setView('map')
    setLookup('idle')
  }

  const showStreetView = () => {
    if (!map || !streetViewLib || view === 'street' || lookup === 'checking') return
    const request = ++requestRef.current
    setLookup('checking')
    new streetViewLib.StreetViewService()
      .getPanorama({ location: at, radius: STREET_VIEW_RADIUS_M })
      .then(({ data }) => {
        if (request !== requestRef.current) return
        const found = data.location
        if (!found?.pano) {
          setLookup('none')
          return
        }
        const panorama = map.getStreetView()
        panorama.setOptions({
          // Both sit top-left, where the switch is; the switch is the way back to the map.
          addressControl: false,
          enableCloseButton: false,
          fullscreenControl: true,
          motionTracking: false,
          motionTrackingControl: false,
        })
        panorama.setPano(found.pano)
        // The panorama can be up to 50 m away: open it looking at the point, not due north.
        const from = found.latLng ? { lat: found.latLng.lat(), lng: found.latLng.lng() } : at
        panorama.setPov({ heading: bearingDegrees(from, at), pitch: 0 })
        panorama.setVisible(true)
        setView('street')
        setLookup('idle')
      })
      .catch(() => {
        // No panorama within range, or Google could not answer: either way, stay on the map.
        if (request === requestRef.current) setLookup('none')
      })
  }

  /** Call from the owner's unmount/cleanup so a late answer is ignored. */
  const cancel = () => {
    requestRef.current += 1
  }

  return {
    view,
    checking: lookup === 'checking',
    unavailable: lookup === 'none',
    ready: !!map && !!streetViewLib,
    showMap,
    showStreetView,
    cancel,
  }
}
